import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { communityServicesAPI } from '../../../services/api';
import UpdateAttendanceModal from './UpdateAttendanceModal.jsx';
import AddVolunteerModal from './AddVolunteerModal.jsx';
import NewCategoryModal from './NewCategoryModal.jsx';
import {
  TrashIcon,
  DocumentIcon,
  UserGroupIcon,
  CalendarIcon,
  MapPinIcon,
  TagIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

function ProgramModal({
  isOpen,
  onClose,
  mode = 'create',
  initialData = null,
  onSuccess,
  programId = null,
}) {
  const isEdit = mode === 'edit';
  const queryClient = useQueryClient();

  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [showAddVolunteerModal, setShowAddVolunteerModal] = useState(false);
  const [showManageCategory, setShowManageCategory] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const [form, setForm] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    location: '',
    organizer: '',
    organizer_id: '',
    category_id: '',
    status: 'planned',
    volunteers: [],
  });

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const AUTO_REFRESH_INTERVAL = 30000;

  const [organizerSearch, setOrganizerSearch] = useState('');
  const [isOrganizerOpen, setIsOrganizerOpen] = useState(false);

  const { data: organizersData, isLoading: isLoadingOrganizers } = useQuery(
    ['community-services', 'search-organizer', organizerSearch],
    async () => {
      const res = await communityServicesAPI.searchOrganizer(organizerSearch);
      const raw = res?.data?.data;

      if (!Array.isArray(raw)) return [];

      return raw
        .map((v) => {
          if (v.resident === null) {
            return {
              id: v?.volunteer_id,
              name: (v?.volunteer_name || '').trim(),
              phone: v?.contact_information || '',
              role: v?.role || '',
            };
          } else {
            return {
              id: v?.volunteer_id,
              name: (v?.resident?.full_name || '').trim(),
              phone: v?.resident?.contact_no || '',
              barangayId: v?.resident?.barangay_id_number || '',
              role: v?.role || '',
            };
          }
        })
        .filter((v) => v.id && v.name);
    },
    {
      enabled: isOpen && isOrganizerOpen && organizerSearch.trim().length > 0,
      keepPreviousData: true,
      staleTime: 0,
      onError: () => {
        toast.error('Failed to search organizers');
      },
    }
  );

  const organizers = organizersData || [];

  const toInputDateTime = (value) => {
    if (!value) return '';
    try {
      if (typeof value === 'string' && value.includes(' ')) {
        const s = value.replace(' ', 'T');
        return s.length >= 16 ? s.slice(0, 16) : s;
      }
      const d = new Date(value);
      if (isNaN(d)) return '';
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const ii = String(d.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}T${hh}:${ii}`;
    } catch {
      return '';
    }
  };

  const toApiDateTime = (localValue) => {
    if (!localValue) return '';
    if (typeof localValue === 'string' && localValue.includes('T')) {
      return localValue.replace('T', ' ').slice(0, 16);
    }
    try {
      const d = new Date(localValue);
      if (isNaN(d)) return '';
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const ii = String(d.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${hh}:${ii}`;
    } catch {
      return '';
    }
  };

  const { data: fetchedProgram } = useQuery(
    ['community-services', 'program', programId],
    async () => {
      try {
        const res = await communityServicesAPI.getProgramById(programId);

        const programData =
          res?.data?.data?.program || res?.data?.data || res?.data;

        return programData;
      } catch (error) {
        console.error(' Error fetching program:', error);
        throw error;
      }
    },
    {
      enabled: Boolean(isOpen && isEdit && programId),
      refetchInterval: autoRefreshEnabled ? AUTO_REFRESH_INTERVAL : false,
      refetchOnWindowFocus: true,
      staleTime: 10000,
      onError: (error) => {
        console.error('Query onError:', error);
      },
      onSuccess: (data) => {
        console.log('Query onSuccess with data:', data);
      },
    }
  );

  useEffect(() => {
    const src = isEdit ? fetchedProgram || initialData : null;
    if (isEdit && src) {
      setForm({
        name: src.name || '',
        description: src.description || '',
        start_date: toInputDateTime(src.start_date) || '',
        end_date: toInputDateTime(src.end_date) || '',
        location: src.location || '',
        organizer: src.organizer || '',
        organizer_id:
          src.organizer_id ||
          src?.organizer?.volunteer_id ||
          src?.organizer?.id ||
          src?.organizer?.user_id ||
          '',
        category_id:
          src.category_id ||
          src?.category?.category_id ||
          src?.category?.id ||
          '',
        status: src.status || 'planned',
        volunteers: src.volunteers || [],
      });
    } else if (!isEdit) {
      setForm({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        location: '',
        organizer: '',
        organizer_id: '',
        category_id: '',
        status: 'planned',
        volunteers: [],
      });
    }
  }, [isEdit, fetchedProgram, initialData, isOpen]);

  const {
    data: categoriesData,
    isLoading: isLoadingCategories,
    error: categoriesError,
  } = useQuery(
    ['community-services', 'categories'],
    async () => {
      const response = await communityServicesAPI.getCategories({
        per_page: 100,
      });
      const { items } = response.data.data;
      return Array.isArray(items)
        ? items.map((it) => ({
            id: it.category_id,
            name: it.name,
            description: it.description,
            status: it.status,
          }))
        : [];
    },
    {
      enabled: isOpen,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onError: (error) => {
        console.error('Failed to load categories:', error);
        toast.error('Failed to load program categories');
      },
    }
  );

  const categories = categoriesData || [];

  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const categoryDropdownRef = useRef(null);
  const [errors, setErrors] = useState({});

  const organizerDropdownRef = useRef(null);
  const selectedOrganizerName = useMemo(() => {
    const found = organizers.find(
      (o) => String(o.id) === String(form.organizer_id)
    );
    if (found) return found.name;

    return (
      fetchedProgram?.organizer?.resident?.full_name ||
      fetchedProgram?.organizer?.full_name ||
      fetchedProgram?.organizer?.name ||
      ''
    );
  }, [organizers, form.organizer_id, fetchedProgram]);

  const selectedCategoryName = useMemo(() => {
    const found = categories.find(
      (c) => String(c.id) === String(form.category_id)
    );
    return found ? found.name : '';
  }, [categories, form.category_id]);

  const toggleCategory = () => setIsCategoryOpen((v) => !v);

  const handleSelectCategory = (id) => {
    setForm((prev) => ({ ...prev, category_id: id }));
    setErrors((prev) => ({ ...prev, category_id: undefined }));
    setIsCategoryOpen(false);
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (!isCategoryOpen) return;
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(e.target)
      ) {
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [isCategoryOpen]);

  const toggleOrganizer = () => {
    setIsOrganizerOpen((v) => !v);
    if (!isOrganizerOpen) {
      setTimeout(() => {
        const searchInput = document.getElementById('organizer-search-input');
        if (searchInput) {
          searchInput.focus();
        }
      }, 10);
    } else {
      setOrganizerSearch('');
    }
  };

  const handleSelectOrganizer = (id) => {
    setForm((prev) => ({ ...prev, organizer_id: id }));
    setErrors((prev) => ({ ...prev, organizer_id: undefined }));
    setIsOrganizerOpen(false);
    setOrganizerSearch('');
  };

  const clearOrganizerSelection = () => {
    setForm((prev) => ({ ...prev, organizer_id: '' }));
    setErrors((prev) => ({ ...prev, organizer_id: undefined }));
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (!isOrganizerOpen) return;
      if (
        organizerDropdownRef.current &&
        !organizerDropdownRef.current.contains(e.target)
      ) {
        setIsOrganizerOpen(false);
        setOrganizerSearch('');
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [isOrganizerOpen]);

  const handleOrganizerSearchChange = (e) => {
    setOrganizerSearch(e.target.value);
  };

  const clearOrganizerSearch = () => {
    setOrganizerSearch('');
  };

  const getAttendanceStatus = (volunteer) => {
    return volunteer?.attendance?.status || null;
  };

  const volunteerDetails = useMemo(
    () =>
      (form.volunteers || []).map((v, index) => {
        const volunteerId = v?.id || v?.volunteer_id || v?.user_id;

        if (v.resident === null) {
          return {
            id: volunteerId || index + 1,
            name: v?.volunteer_name || v?.name || '',
            userId: `VOL-${volunteerId || index + 1}`,
            role: v?.role || '',
            initials: (v?.volunteer_name || v?.name || '')
              .split(' ')
              .filter(Boolean)
              .map((n) => n[0])
              .join('')
              .toUpperCase(),
            raw: v,
          };
        } else {
          const name = (
            v?.resident?.full_name ||
            v?.full_name ||
            v?.name ||
            ''
          ).trim();
          const userId =
            v?.resident?.barangay_id_number || v?.barangay_id_no || '';
          const role = v?.role || '';
          return {
            id: volunteerId || index + 1,
            name,
            userId,
            role,
            initials: name
              .split(' ')
              .filter(Boolean)
              .map((n) => n[0])
              .join('')
              .toUpperCase(),
            raw: v,
          };
        }
      }),
    [form.volunteers]
  );

  console.log("VOLUNTEER DETAILS COMM: ", volunteerDetails)
  const handleRemoveVolunteer = (vol) => {
    const keyOf = (v) =>
      String(
        v?.id ||
          v?.volunteer_id ||
          v?.resident?.resident_id ||
          v?.user_id ||
          JSON.stringify(v)
      );
    const targetKey = keyOf(vol);
    setForm((prev) => ({
      ...prev,
      volunteers: (prev.volunteers || []).filter((v) => keyOf(v) !== targetKey),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = () => {
    const errs = {};
    if (!form.name?.trim()) errs.name = 'Program name is required';
    if (!form.category_id) errs.category_id = 'Program category is required';
    if (!form.start_date) errs.start_date = 'Start date is required';
    if (!form.end_date) errs.end_date = 'End date is required';
    if (!form.location?.trim()) errs.location = 'Location is required';
    if (!form.status) errs.status = 'Status is required';

    if (form.start_date && form.end_date) {
      const s = new Date(form.start_date);
      const e = new Date(form.end_date);
      if (!isNaN(s) && !isNaN(e) && e < s) {
        errs.end_date = 'End date cannot be earlier than start date';
      }
    }
    return errs;
  };

  const { mutate: createProgram, isLoading: isCreating } = useMutation(
    (payload) => communityServicesAPI.createProgram(payload),
    {
      onSuccess: (res) => {
        toast.success('Program created');

        queryClient.invalidateQueries(['community-services']);
        queryClient.invalidateQueries(['community-services', 'program']);
        queryClient.invalidateQueries({
          queryKey: ['community-services'],
          type: 'all',
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'community-services' ||
            (Array.isArray(query.queryKey) &&
              query.queryKey[0] === 'community-services'),
        });

        onSuccess?.(res?.data?.data || payload);
        onClose?.();
      },
      onError: (error) => {
        console.error('Failed to create program:', error);
        toast.error('Failed to create program');
      },
    }
  );

  const { mutate: updateProgram, isLoading: isUpdating } = useMutation(
    ({ id, data }) => communityServicesAPI.updateProgram(id, data),
    {
      onSuccess: (res) => {
        toast.success('Program updated');

        queryClient.invalidateQueries(['community-services']);
        queryClient.invalidateQueries([
          'community-services',
          'program',
          programId,
        ]);
        queryClient.invalidateQueries({
          queryKey: ['community-services'],
          type: 'all',
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'community-services' ||
            (Array.isArray(query.queryKey) &&
              query.queryKey[0] === 'community-services'),
        });

        onSuccess?.(
          res?.data?.data || {
            ...form,
            program_id:
              fetchedProgram?.program_id ||
              initialData?.program_id ||
              programId,
          }
        );
        onClose?.();
      },
      onError: (error) => {
        console.error('Failed to update program:', error);
        toast.error('Failed to update program');
      },
    }
  );

  const handleSubmit = () => {
    const v = validateForm();
    if (Object.keys(v).length) {
      setErrors(v);
      const firstMsg = Object.values(v)[0];
      if (firstMsg) toast.error(String(firstMsg));
      return;
    }

    const payload = {
      name: form.name,
      description: form.description,
      start_date: toApiDateTime(form.start_date),
      end_date: toApiDateTime(form.end_date),
      location: form.location,
      organizer_id: form.organizer_id || undefined,
      category_id: form.category_id || undefined,
      status: form.status,
      volunteer_ids: (form.volunteers || [])
        .map(
          (v) =>
            v?.id || v?.volunteer_id || v?.user_id || v?.resident?.resident_id
        )
        .filter(Boolean),
    };

    const id =
      initialData?.program_id || fetchedProgram?.program_id || programId;
    if (isEdit && id) {
      updateProgram({ id, data: payload });
    } else {
      createProgram(payload);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 animate-in fade-in duration-500"> */}
      <div className='fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center'>
        {/* <div className="relative mx-auto p-6 border w-full max-w-4xl shadow-2xl rounded-lg bg-white my-10 animate-in slide-in-from-bottom duration-700"> */}
        <div className='relative w-full max-w-4xl bg-white rounded-lg shadow-2xl flex flex-col max-h-[90vh]'>
          {/* <div className="flex justify-between items-center mb-6 animate-in slide-in-from-top duration-500 delay-200"> */}
          <div className='flex justify-between items-center p-6 border-b border-gray-200'>
            <h3 className='text-xl font-medium text-gray-900'>
              {isEdit ? 'Edit Program' : 'Add New Program'}
            </h3>
            <button
              type='button'
              onClick={onClose}
              className='text-gray-400 hover:text-gray-600 text-2xl transition-all duration-300 transform hover:scale-125 hover:rotate-90 active:scale-95'
            >
              ×
            </button>
          </div>

          {/* Program Details Section */}
          <div className='overflow-y-auto px-6 py-4 flex-1'>
            {/* style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', }} */}
            <div className='mb-8'>
              <div className='flex items-center gap-2 mb-4'>
                <DocumentIcon className='size-5 text-blue-600' />
                <h3 className='font-semibold text-gray-900'>Program Details</h3>
              </div>

              <div className='space-y-6'>
                {/* Row 1: Program Name and Category - FIXED ALIGNMENT */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  {/* Program Name Field */}
                  <div className='flex flex-col'>
                    <label className='block text-sm font-medium text-gray-700 mb-3'>
                      Program Name <span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='text'
                      name='name'
                      value={form.name}
                      onChange={handleInputChange}
                      placeholder='Enter program name (e.g., Bayanihan sa Barangay)'
                      className={`w-full border-2 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.name
                          ? 'border-red-500'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    />
                    {errors.name && (
                      <p className='mt-1 text-xs text-red-600'>{errors.name}</p>
                    )}
                  </div>

                  {/* Program Category Field - FIXED ALIGNMENT */}
                  <div className='flex flex-col'>
                    <div className='flex items-center justify-between mb-2'>
                      <label className='text-sm font-medium text-gray-700'>
                        Program Category <span className='text-red-500'>*</span>
                      </label>
                      <button
                        type='button'
                        onClick={() => setShowManageCategory(true)}
                        className='px-2 py-1 text-xs text-blue-600 hover:text-blue-700 font-medium'
                      >
                        + New Program Category
                      </button>
                    </div>
                    <div className='relative' ref={categoryDropdownRef}>
                      <button
                        type='button'
                        onClick={toggleCategory}
                        className={`w-full text-left border-2 rounded-lg px-4 py-3 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.category_id
                            ? 'border-red-500'
                            : 'border-gray-200 hover:border-gray-300'
                        } ${
                          selectedCategoryName ||
                          fetchedProgram?.category?.name ||
                          initialData?.category?.name
                            ? 'text-gray-900'
                            : 'text-gray-400'
                        }`}
                      >
                        {selectedCategoryName ||
                          fetchedProgram?.category?.name ||
                          initialData?.category?.name ||
                          'Select a category'}
                      </button>
                      {errors.category_id && (
                        <p className='mt-1 text-xs text-red-600'>
                          {errors.category_id}
                        </p>
                      )}

                      {isCategoryOpen && (
                        <div
                          className='absolute z-50 mt-1 w-full max-h-60 overflow-auto border border-gray-200 bg-white rounded-md shadow-lg'
                          role='listbox'
                          aria-label='Program categories'
                        >
                          {isLoadingCategories && (
                            <div className='px-3 py-2 text-sm text-gray-500'>
                              Loading categories...
                            </div>
                          )}
                          {!categories.length && !isLoadingCategories && (
                            <div className='px-3 py-2 text-sm text-gray-500'>
                              No categories found
                            </div>
                          )}
                          {categories.map((category) => (
                            <div
                              key={category.id}
                              role='option'
                              aria-selected={
                                String(form.category_id) === String(category.id)
                              }
                              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                                String(form.category_id) === String(category.id)
                                  ? 'bg-gray-50'
                                  : ''
                              }`}
                              onClick={() => handleSelectCategory(category.id)}
                            >
                              {category.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Row 2: Description */}
                <div className='flex flex-col'>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Description
                  </label>
                  <textarea
                    name='description'
                    value={form.description}
                    onChange={handleInputChange}
                    rows='4'
                    placeholder='Enter a brief program description (e.g., A tree planting activity to promote environmental awareness)'
                    className='w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 transition-all duration-200 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  />
                </div>

                {/* Row 3: Start Date and End Date */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <div className='flex flex-col'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Start Date & Time <span className='text-red-500'>*</span>
                    </label>
                    <div className='relative'>
                      <input
                        type='datetime-local'
                        name='start_date'
                        value={form.start_date}
                        onChange={handleInputChange}
                        className={`w-full border-2 rounded-lg px-4 py-3 text-gray-900 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.start_date
                            ? 'border-red-500'
                            : 'border-gray-200 hover:border-gray-300'
                        } ${
                          !form.start_date ? 'text-gray-400' : 'text-gray-900'
                        }`}
                      />
                      {!form.start_date && (
                        <div className='absolute inset-0 pointer-events-none flex items-center px-4 py-3'>
                          {/* <span className="text-gray-400">Select date and time</span> */}
                        </div>
                      )}
                    </div>
                    {errors.start_date && (
                      <p className='mt-1 text-xs text-red-600'>
                        {errors.start_date}
                      </p>
                    )}
                  </div>

                  <div className='flex flex-col'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      End Date & Time <span className='text-red-500'>*</span>
                    </label>
                    <div className='relative'>
                      <input
                        type='datetime-local'
                        name='end_date'
                        value={form.end_date}
                        onChange={handleInputChange}
                        className={`w-full border-2 rounded-lg px-4 py-3 text-gray-900 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.end_date
                            ? 'border-red-500'
                            : 'border-gray-200 hover:border-gray-300'
                        } ${
                          !form.end_date ? 'text-gray-400' : 'text-gray-900'
                        }`}
                      />
                      {!form.end_date && (
                        <div className='absolute inset-0 pointer-events-none flex items-center px-4 py-3'>
                          {/* <span className="text-gray-400">Select date and time</span> */}
                        </div>
                      )}
                    </div>
                    {errors.end_date && (
                      <p className='mt-1 text-xs text-red-600'>
                        {errors.end_date}
                      </p>
                    )}
                  </div>
                </div>

                {/* Row 4: Location (full width) */}
                <div className='flex flex-col'>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Location <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    name='location'
                    value={form.location}
                    onChange={handleInputChange}
                    placeholder='Enter location (e.g., Barangay Hall, Brgy. San Isidro)'
                    className={`w-full border-2 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.location
                        ? 'border-red-500'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  />
                  {errors.location && (
                    <p className='mt-1 text-xs text-red-600'>
                      {errors.location}
                    </p>
                  )}
                </div>

                {/* Row 5: Status Field */}
                <div className='flex flex-col'>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Status <span className='text-red-500'>*</span>
                  </label>
                  <div className='relative'>
                    <select
                      name='status'
                      value={form.status}
                      onChange={handleInputChange}
                      className={`w-full border-2 rounded-lg px-4 py-3 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none ${
                        errors.status
                          ? 'border-red-500'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${
                        !form.status || form.status === 'planned'
                          ? 'text-gray-400'
                          : 'text-gray-900'
                      }`}
                    >
                      <option value='' disabled className='text-gray-400'>
                        Select status
                      </option>
                      <option value='planned' className='text-gray-900'>
                        Planned
                      </option>
                      <option value='ongoing' className='text-gray-900'>
                        Ongoing
                      </option>
                      <option value='completed' className='text-gray-900'>
                        Completed
                      </option>
                      <option value='cancelled' className='text-gray-900'>
                        Cancelled
                      </option>
                    </select>
                    <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
                      <svg
                        className='w-5 h-5 text-gray-400'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M19 9l-7 7-7-7'
                        />
                      </svg>
                    </div>
                  </div>
                  {errors.status && (
                    <p className='mt-1 text-xs text-red-600'>{errors.status}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Organizer & Volunteers Section */}
            <div className='mb-8'>
              <div className='flex items-center gap-2 mb-4'>
                <UserGroupIcon className='size-5 text-blue-600' />
                <h3 className='font-semibold text-gray-900'>
                  Organizer & Volunteers
                </h3>
              </div>

              <div className='space-y-6'>
                {/* Organizer Name with Searchable Dropdown */}
                <div className='flex flex-col'>
                  <div className='flex items-center justify-between mb-2'>
                    <label className='text-sm font-medium text-gray-700'>
                      Organizer Name <span className='text-red-500'>*</span>
                    </label>
                    {form.organizer_id && (
                      <button
                        type='button'
                        onClick={clearOrganizerSelection}
                        className='text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1'
                      >
                        <XMarkIcon className='w-3 h-3' />
                        Clear
                      </button>
                    )}
                  </div>
                  <div className='relative' ref={organizerDropdownRef}>
                    <button
                      type='button'
                      onClick={toggleOrganizer}
                      className={`w-full text-left border-2 rounded-lg px-4 py-3 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.organizer_id
                          ? 'border-red-500'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${
                        selectedOrganizerName ||
                        fetchedProgram?.organizer?.resident?.full_name ||
                        fetchedProgram?.organizer?.full_name ||
                        fetchedProgram?.organizer?.name ||
                        initialData?.organizer?.resident?.full_name ||
                        initialData?.organizer?.full_name ||
                        initialData?.organizer?.name
                          ? 'text-gray-900'
                          : 'text-gray-400'
                      }`}
                    >
                      {selectedOrganizerName ||
                        fetchedProgram?.organizer?.resident?.full_name ||
                        fetchedProgram?.organizer?.full_name ||
                        fetchedProgram?.organizer?.name ||
                        initialData?.organizer?.resident?.full_name ||
                        initialData?.organizer?.full_name ||
                        initialData?.organizer?.name ||
                        'Select Organizer'}
                    </button>
                    {errors.organizer_id && (
                      <p className='mt-1 text-xs text-red-600'>
                        {errors.organizer_id}
                      </p>
                    )}

                    {isOrganizerOpen && (
                      <div
                        className='absolute z-50 mt-1 w-full border border-gray-200 bg-white rounded-md shadow-lg'
                        role='listbox'
                        aria-label='Organizers'
                      >
                        {/* Search Input */}
                        <div className='sticky top-0 bg-white p-2 border-b border-gray-200'>
                          <div className='relative'>
                            <input
                              id='organizer-search-input'
                              type='text'
                              value={organizerSearch}
                              onChange={handleOrganizerSearchChange}
                              placeholder='Search by name, email, phone...'
                              className='w-full px-3 py-2 pl-9 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400'
                              autoFocus
                            />
                            <MagnifyingGlassIcon className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400' />
                            {organizerSearch && (
                              <button
                                type='button'
                                onClick={clearOrganizerSearch}
                                className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600'
                              >
                                <XMarkIcon className='w-4 h-4' />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className='max-h-48 overflow-auto'>
                          {isLoadingOrganizers && (
                            <div className='px-3 py-4 text-sm text-gray-500 text-center'>
                              Loading organizers...
                            </div>
                          )}

                          {!isLoadingOrganizers && (
                            <>
                              {/* Search Results */}
                              {organizers.length === 0 &&
                              !isLoadingOrganizers ? (
                                <div className='px-3 py-4 text-sm text-gray-500 text-center'>
                                  {organizerSearch.trim()
                                    ? 'No organizers found'
                                    : 'No organizers available'}
                                </div>
                              ) : (
                                organizers.map((organizer) => (
                                  <div
                                    key={organizer.id}
                                    role='option'
                                    aria-selected={
                                      String(form.organizer_id) ===
                                      String(organizer.id)
                                    }
                                    className={`px-3 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                                      String(form.organizer_id) ===
                                      String(organizer.id)
                                        ? 'bg-blue-50'
                                        : ''
                                    }`}
                                    onClick={() =>
                                      handleSelectOrganizer(organizer.id)
                                    }
                                  >
                                    <div className='flex items-center justify-between'>
                                      <div>
                                        <div className='font-medium text-gray-900'>
                                          {organizer.name}
                                        </div>
                                        <div className='text-xs text-gray-500 mt-1 space-y-1'>
                                          {organizer.email && (
                                            <div className='flex items-center gap-1'>
                                              <svg
                                                className='w-3 h-3 text-gray-400'
                                                fill='none'
                                                stroke='currentColor'
                                                viewBox='0 0 24 24'
                                                xmlns='http://www.w3.org/2000/svg'
                                              >
                                                <path
                                                  strokeLinecap='round'
                                                  strokeLinejoin='round'
                                                  strokeWidth={2}
                                                  d='M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                                                />
                                              </svg>
                                              {organizer.email}
                                            </div>
                                          )}
                                          {organizer.phone && (
                                            <div className='flex items-center gap-1'>
                                              <svg
                                                className='w-3 h-3 text-gray-400'
                                                fill='none'
                                                stroke='currentColor'
                                                viewBox='0 0 24 24'
                                                xmlns='http://www.w3.org/2000/svg'
                                              >
                                                <path
                                                  strokeLinecap='round'
                                                  strokeLinejoin='round'
                                                  strokeWidth={2}
                                                  d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
                                                />
                                              </svg>
                                              {organizer.phone}
                                            </div>
                                          )}
                                          {organizer.barangayId && (
                                            <div className='flex items-center gap-1'>
                                              <svg
                                                className='w-3 h-3 text-gray-400'
                                                fill='none'
                                                stroke='currentColor'
                                                viewBox='0 0 24 24'
                                                xmlns='http://www.w3.org/2000/svg'
                                              >
                                                <path
                                                  strokeLinecap='round'
                                                  strokeLinejoin='round'
                                                  strokeWidth={2}
                                                  d='M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2'
                                                />
                                              </svg>
                                              ID: {organizer.barangayId}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {String(form.organizer_id) ===
                                        String(organizer.id) && (
                                        <svg
                                          className='w-5 h-5 text-blue-600'
                                          fill='none'
                                          stroke='currentColor'
                                          viewBox='0 0 24 24'
                                          xmlns='http://www.w3.org/2000/svg'
                                        >
                                          <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth={2}
                                            d='M5 13l4 4L19 7'
                                          />
                                        </svg>
                                      )}
                                    </div>
                                  </div>
                                ))
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Volunteer Selection */}
                <div className='flex flex-col'>
                  <div className='flex justify-between items-center mb-3'>
                    <label className='text-sm font-medium text-gray-700'>
                      Volunteer Selection
                    </label>
                    <div className='flex items-center gap-3'>
                      {isEdit && (
                        <button
                          type='button'
                          onClick={() => setShowAttendanceModal(true)}
                          className='flex items-center gap-1 text-green-600 hover:text-green-700 text-sm font-medium'
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                            strokeWidth={1.5}
                            stroke='currentColor'
                            className='size-4'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              d='M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z'
                            />
                          </svg>
                          Manage Attendance
                        </button>
                      )}
                      <button
                        type='button'
                        onClick={() => setShowAddVolunteerModal(true)}
                        className='text-[#2563EB] hover:text-blue-700 font-medium text-sm transition-colors'
                      >
                        + Add Volunteer
                      </button>
                    </div>
                  </div>

                  <div
                    className={`rounded-lg border-2 ${
                      volunteerDetails.length === 0
                        ? 'border-dashed border-gray-300 p-6 text-center bg-gray-50'
                        : 'border-gray-200'
                    }`}
                  >
                    {volunteerDetails.length === 0 ? (
                      <div className='flex flex-col items-center justify-center py-8'>
                        <UserGroupIcon className='size-8 text-gray-400 mb-3' />
                        <p className='text-gray-500 text-sm'>
                          No volunteers added yet
                        </p>
                      </div>
                    ) : (
                      <div className='space-y-3 p-1'>
                        {volunteerDetails.map((volunteer) => {
                          const attendanceStatus = getAttendanceStatus(volunteer.raw);
                          
                          return (
                            <div
                              key={volunteer.id}
                              className='border border-gray-200 rounded-lg p-4 flex items-center justify-between'
                            >
                              <div className='flex items-start gap-3 flex-1'>
                                <div className='flex-shrink-0'>
                                  <div className='w-8 h-8 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center text-xs font-medium text-white'>
                                    {volunteer.initials}
                                  </div>
                                </div>
                                <div className='flex-1 min-w-0'>
                                  <p className='text-sm font-medium text-gray-900 mb-1'>
                                    {volunteer.name}
                                  </p>
                                  <div className='flex items-center gap-2 text-xs text-gray-500 font-semibold'>
                                    <span className='px-2 py-1 rounded-full bg-blue-100 text-blue-800'>
                                      {volunteer.userId}
                                    </span>
                                    <span>•</span>
                                    
                                    {/* Attendance Status - Replaced volunteer status */}
                                    {attendanceStatus ? (
                                      <span className={`px-2 py-1 rounded-full ${
                                        attendanceStatus === "present" 
                                          ? "bg-blue-100 text-blue-800" 
                                          : attendanceStatus === "absent" 
                                          ? "bg-red-100 text-red-800" 
                                          : "bg-gray-100 text-gray-800"
                                      }`}>
                                        {attendanceStatus}
                                      </span>
                                    ) : (
                                      <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-500">
                                        no attendance
                                      </span>
                                    )}
                                    
                                    <span>•</span>
                                    <span>{volunteer.role}</span>
                                  </div>
                                </div>
                              </div>
                              <button
                                type='button'
                                onClick={() =>
                                  handleRemoveVolunteer(volunteer.raw)
                                }
                                className='ml-4 text-red-600 hover:text-red-700 p-1.5 rounded-full hover:bg-red-50 transition-all duration-200'
                                title='Remove volunteer'
                              >
                                <TrashIcon
                                  className='w-5 h-5'
                                  aria-hidden='true'
                                />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className='-mx-6 border-t border-gray-200 pt-6'></div>

            {/* Buttons */}
            <div className='flex justify-end gap-4 pt-6'>
              <button
                type='button'
                onClick={onClose}
                className='px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200'
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isCreating || isUpdating}
                className='px-6 py-3 border-2 border-transparent rounded-lg text-white font-medium bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 relative overflow-hidden group'
              >
                <span
                  className={`transition-all duration-300 ${
                    isCreating || isUpdating ? 'opacity-0' : 'opacity-100'
                  }`}
                >
                  {isEdit ? 'Save Changes' : 'Schedule Program'}
                </span>
                {(isCreating || isUpdating) && (
                  <span className='absolute inset-0 flex items-center justify-center'>
                    <svg
                      className='animate-spin h-5 w-5 text-white'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                    >
                      <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'
                      ></circle>
                      <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                      ></path>
                    </svg>
                  </span>
                )}
                <div className='absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300'></div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <UpdateAttendanceModal
        isOpen={showAttendanceModal}
        onClose={() => setShowAttendanceModal(false)}
        programId={initialData?.program_id || programId}
        volunteers={volunteerDetails}
        onUpdate={() => {
          setShowAttendanceModal(false);
          queryClient.invalidateQueries([
            'community-services',
            'program',
            programId,
          ]);
        }}
      />

      <AddVolunteerModal
        isOpen={showAddVolunteerModal}
        onClose={() => setShowAddVolunteerModal(false)}
        onAddVolunteer={(volunteerData) => {
          const arr = Array.isArray(volunteerData)
            ? volunteerData
            : [volunteerData];
          setForm((prev) => {
            const existing = prev.volunteers || [];
            const keyOf = (v) =>
              String(
                v?.volunteer_id ??
                  v?.resident?.resident_id ??
                  v?.user_id ??
                  v?.id ??
                  JSON.stringify(v)
              );
            const seen = new Set(existing.map(keyOf));
            const merged = [...existing];
            for (const v of arr) {
              const k = keyOf(v);
              if (!seen.has(k)) {
                seen.add(k);
                merged.push(v);
              }
            }
            return { ...prev, volunteers: merged };
          });
          setShowAddVolunteerModal(false);
        }}
      />

      {/* New Program Category Modal */}
      <NewCategoryModal
        isOpen={showManageCategory}
        onCategoryAdded={(newCategory) => {
          setForm((prev) => ({
            ...prev,
            category_id: newCategory.id || newCategory.category_id,
          }));
          queryClient.invalidateQueries(['programCategories']);
        }}
        onClose={() => {
          setShowManageCategory(false);
          queryClient.invalidateQueries(['community-services', 'categories']);
        }}
      />
    </>
  );
}

export default ProgramModal;